import React, {useCallback, useEffect, useRef, useState} from "react";
import {ColumnFilterProps} from "./types";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {useDropdownPosition} from "@/hooks/useDropdownPosition";

type TimeRange = "12h" | "24h" | "1w" | "1m";

const TIME_RANGES = [
    { value: "12h", label: "Last 12 Hours" },
    { value: "24h", label: "Last 24 Hours" },
    { value: "1w", label: "Last Week" },
    // { value: "1m", label: "Last Month" },
];

export const TimeRangeFilter: React.FC<ColumnFilterProps> = ({label, onChange, filterParams}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const position = useDropdownPosition(isOpen, dropdownRef, 300);

    const getInitialTimeRange = useCallback((): TimeRange => {
        if (filterParams.timeRange && (filterParams.timeRange === "12h" || filterParams.timeRange === "24h" || filterParams.timeRange === "1w" || filterParams.timeRange === "1m")) {
            return filterParams.timeRange as TimeRange;
        }
        return "24h"; // Default to 24 hours
    }, [filterParams.timeRange]);

    // Applied state (what's currently in the filter)
    const [appliedTimeRange, setAppliedTimeRange] = useState<TimeRange>(getInitialTimeRange);

    // Staged state (what the user is selecting)
    const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>(getInitialTimeRange);

    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isOpen) {
            // When opening, sync staged state with applied state
            setSelectedTimeRange(appliedTimeRange);
        }
        setIsOpen(!isOpen);
    }, [isOpen, appliedTimeRange]);

    const handleSelectTimeRange = useCallback((range: TimeRange, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedTimeRange(range);
    }, []);

    const handleApply = useCallback(() => {
        setAppliedTimeRange(selectedTimeRange);
        onChange({timeRange: selectedTimeRange});
        setIsOpen(false);
    }, [selectedTimeRange, onChange]);

    const handleClear = useCallback(() => {
        setSelectedTimeRange("24h");
        setAppliedTimeRange("24h");
        onChange({timeRange: "24h"});
    }, [onChange]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Update state when filterParams change (e.g., from URL)
    useEffect(() => {
        const newTimeRange = getInitialTimeRange();
        setAppliedTimeRange(newTimeRange);
        setSelectedTimeRange(newTimeRange);
    }, [getInitialTimeRange]);

    // Set default on mount if no filter is set
    useEffect(() => {
        if (!filterParams.timeRange) {
            onChange({timeRange: "24h"});
        }
    }, []); // Only run on mount

    const getDisplayLabel = () => {
        const range = TIME_RANGES.find(r => r.value === appliedTimeRange);
        return range ? range.label : "Last 24 Hours";
    };

    const displayLabel = getDisplayLabel();

    return (
        <div className="relative inline-block" ref={dropdownRef}>
            <button
                onClick={handleToggle}
                className="flex items-center gap-1 hover:text-blue-600 transition-colors font-bold"
            >
                <span>{label}</span>
                <span className="text-xs text-blue-600 font-normal">
                    ({displayLabel})
                </span>
                <span className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
            </button>

            {isOpen && (
                <div className={`absolute ${position} mt-1 bg-white rounded shadow-lg w-[250px] flex flex-col z-[1000]`} onClick={(e) => e.stopPropagation()}>
                    <div className="max-h-[300px] overflow-y-auto">
                        {TIME_RANGES.map((range) => (
                            <button
                                key={range.value}
                                onClick={(e) => handleSelectTimeRange(range.value as TimeRange, e)}
                                className="w-full text-left px-4 py-2 hover:bg-gray-100 font-normal flex items-center gap-2"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedTimeRange === range.value}
                                    onChange={() => {}}
                                    className="cursor-pointer w-4 h-4"
                                />
                                <span>{range.label}</span>
                            </button>
                        ))}
                    </div>
                    <div className="p-3 flex gap-2">
                        <div className="flex-1">
                            <ButtonV2 label="Clear" color="gray" onClick={handleClear} />
                        </div>
                        <div className="flex-1">
                            <ButtonV2 label="Apply Filter" color="blue" onClick={handleApply} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
